/**
 * EmbedsCategoryPanel (Story 37.3 — Phase 2 category panel #2)
 *
 * The `embeds` sidebar category's floating-panel body. Replaces the generic
 * `CardListPanel` with a purpose-built **configure-then-add** picker: the user
 * pastes a URL + presses Preview (which auto-detects the provider via the shared
 * `transformUrl` and produces an `embedUrl`), configures aspect ratio / title /
 * border, then Add attaches the populated config to a new `embed` widget so it
 * renders non-blank (AC #2, AC #4).
 *
 * Reuse, don't fork: the URL builder + HTTPS-only guard + allowed-domain warning
 * + provider auto-detect are the SHARED `EmbedConfigSection` from
 * `widgetMediaForms` (the exact same sub-form `WidgetConfigPanel` uses when
 * editing an existing embed — AC #3 anti-drift). The sub-form's contract is
 * `{ widget, onUpdate }`; this panel wraps a local draft config in a synthetic
 * draft-widget and adapts `onUpdate` to merge patches into the draft (Pre-mortem
 * A).
 *
 * Add is DISABLED until `draft.embedUrl` is non-empty (the user pressed Preview
 * on a valid HTTPS URL) — the concrete "never blank" enforcement.
 */

import { useState, useMemo, useCallback } from "react";
import { Code } from "lucide-react";
import type { WidgetConfig } from "../../lib/templateUtils";
import { DEFAULT_WIDGET_CONFIGS } from "./canvasWidgetDefaults";
import { EmbedConfigSection } from "./widgetMediaForms";
import type { CategoryPanelProps } from "./categoryPickerRegistry";

/** Synthetic draft-widget id — stable, never persisted (Pre-mortem A). */
const DRAFT_EMBED_WIDGET_ID = "__draft_embed__";

export function EmbedsCategoryPanel({ onWidgetAdd }: CategoryPanelProps) {
  // Seed the draft from the real default config (Pre-mortem D: exactly the
  // `embed` shape — no `height`/`embedType` — so a configured-then-added embed
  // equals what a blank drop + manual fill produces).
  const [draft, setDraft] = useState<Record<string, unknown>>(() => ({
    ...DEFAULT_WIDGET_CONFIGS.embed,
  }));

  const draftWidget = useMemo<WidgetConfig>(
    () => ({
      id: DRAFT_EMBED_WIDGET_ID,
      type: "embed" as const,
      title: "Embedded Content",
      position: { row: 0, col: 0, colSpan: 12, rowSpan: 3 },
      config: draft,
    }),
    [draft],
  );

  // Adapt the sub-form's `onUpdate(id, changes)` to merge `changes.config` into
  // the local draft (Pre-mortem A: the merge MUST land or Add stays disabled).
  const handleDraftUpdate = useCallback(
    (_id: string, changes: Partial<WidgetConfig>) => {
      if (!changes.config) return;
      setDraft((d) => ({ ...d, ...(changes.config as Record<string, unknown>) }));
    },
    [],
  );

  const embedUrl = (draft.embedUrl as string) ?? "";
  // Add is enabled only once a valid HTTPS URL was committed via Preview
  // (embedUrl set) AND a real add handler is wired — a missing `onWidgetAdd`
  // would otherwise leave a button that wipes the draft and does nothing
  // (code-review R1).
  const canAdd = embedUrl.length > 0 && typeof onWidgetAdd === "function";

  const handleAdd = useCallback(() => {
    if (!canAdd || !onWidgetAdd) return;
    const added = onWidgetAdd("embed", { ...draft });
    // Reset the draft ONLY if the widget was actually added — a rejected add
    // (page at MAX_WIDGETS) must not discard the committed URL (code-review R1).
    if (added) setDraft({ ...DEFAULT_WIDGET_CONFIGS.embed });
  }, [canAdd, onWidgetAdd, draft]);

  return (
    <div className="flex h-full flex-col p-3" data-testid="embeds-category-panel">
      <div className="mb-3 flex items-center gap-2">
        <Code size={18} className="text-muted-foreground" />
        <h3 className="text-[13px] font-semibold text-foreground">Embeds</h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        <EmbedConfigSection widget={draftWidget} onUpdate={handleDraftUpdate} />
      </div>

      <button
        type="button"
        data-testid="embeds-panel-add-btn"
        disabled={!canAdd}
        onClick={handleAdd}
        className="mt-3 w-full rounded-md border border-input bg-primary px-3 py-2 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Add to report
      </button>
    </div>
  );
}
